var _ = require("../../uam/module.js")(module.exports, {
  namespace: "egt.reserve.thankyou",
  title: "EGT Reserve Thank You",
  description: "The /reserve/thank-you arrival page — a founder's own $9.57 reservation, shown from the client's own session (no PII in the URL).",
  supportServer: true,
  fields: {}
});

var S = require("../helpers/egt.sections.js");

/*!
 * /reserve/thank-you — WHERE A SUCCESSFUL $9.57 RESERVATION LANDS (F15c).
 *
 * The public-beta reserve flow used to end IN-PAGE, in a dismissable
 * "Saved your spot!" overlay stacked on top of the payment wizard. This brings
 * it up to the /exploit/thank-you bar: a real destination — a designed arrival
 * page with a video hero and a receipt of the reservation — that reads as a
 * moment, not a toast. Founder ruling 2026-07-25: "reserve needs to match the
 * preorder form. Match the polish!".
 *
 * IT DELIBERATELY REUSES the exploit thank-you's standalone `.egt-ty__*` skin
 * (app/scss/egt.exploit.thankyou.scss is scoped entirely to those classes and
 * borrows the shared EGT system) so the two arrival pages read as one system
 * with no new stylesheet to drift.
 *
 * HOW THE RECEIPT RESOLVES (F15 — now at full exploit parity). The client
 * (app/public/src/egt.reserve.thankyou.js) fills the slots from two sources, in
 * order, EXACTLY MIRRORING the exploit preorder thank-you:
 *   1. SAME-SESSION FAST PATH — the success handoff in
 *      app/public/src/post-keynote-page.js writes the reserver's own receipt to
 *      sessionStorage and navigates here; the client reads it back with no
 *      network. Nothing buyer-specific is in the URL on this path.
 *   2. DURABLE TOKEN PATH — the status poll (keynote.reserve.js
 *      getReservationStatusAsync) mints an opaque 256-bit server token and the
 *      handoff carries it as `/reserve/thank-you?t=<token>`. With no session
 *      receipt, the client POSTs the token to /api/keynote-reserve/receipt and
 *      fills the slots from the server's masked view. This is what makes the page
 *      survive a fresh tab, a bookmark, or a redirect-based wallet return.
 *   3. NEITHER resolves → the neutral "we can't bring up a reservation" state,
 *      which points at the confirmation email — never an error.
 * The URL carries ONLY the opaque token — no PII, nothing to enumerate. The
 * token store (app/models/egt.keynote.reserve.receipts.js) holds no PII either;
 * it maps token -> reservation id, and the endpoint does the masked lookup.
 *
 * THE PAGE IS STATIC; THE RECEIPT IS NOT. Every EGT surface is prebuilt to
 * app/cache/app/pages/ and served as bytes, so nothing buyer-specific renders
 * here at build time. What ships is a scaffold of empty `data-ty-slot` slots and
 * three states swapped by the client:
 *   [data-ty-state="loading"]  the shipped default + no-JS state
 *   [data-ty-state="order"]    the resolved receipt
 *   [data-ty-state="missing"]  no session receipt (fresh tab / stale link)
 *
 * NO DARK SCRIM OVER THE MEDIA (agents.md §4). Contrast comes from the frosted
 * card alone; the hero video underneath is never dimmed.
 */
exports.render = async function render() {
  return S.page("/reserve", [
    robotsNoindex(),
    renderReservation(),
    renderNext(),
    renderClose()
  ], { hideNavCta: true });
};

/* KEEP THIS PAGE OUT OF SEARCH, BUT SERVING (SEO wave 2026-10-09, 2A-5).
 * A post-reservation arrival page has nothing for a searcher, so it should not be
 * indexed. The framework's `"noindex": true` page flag is NOT used: it drops the
 * page from the serving manifest and the URL 404s, which would strand every
 * reserver's redirect. A robots.txt Disallow is NOT used either: Google would then
 * never fetch the page and never see the noindex. Instead this ships a plain
 * robots meta in the HTML, and the page client (app/public/src/
 * egt.reserve.thankyou.js) moves it into <head> on load, so the page keeps
 * answering 200 for the reserve flow. "follow" keeps the links out crawlable. */
function robotsNoindex() {
  return { n: "meta", name: "robots", content: "noindex, follow" };
}

/* A slot the client fills. Rendered with placeholder text (not empty) so the
 * scaffold has real height before the swap and the card does not grow under the
 * reader. */
function slot(key, cls, placeholder) {
  return { n: "span", c: cls, "data-ty-slot": key, i: placeholder || "" };
}

/* S.glass() returns a fixed shape and is shared by every surface, so it is not
 * the place to add a pass-through — decorate the node it returns instead (same
 * card, same classes, plus this page's state hooks). Mirrors the exploit page. */
function withAttrs(node, attrs) {
  Object.keys(attrs).forEach(function (k) { node[k] = attrs[k]; });
  return node;
}

/* ── THE RESERVATION SECTION — arrival AND receipt, one integrated card, over a
 * VIDEO hero of the platform the buyer just reserved a lane on.
 *
 * FOUNDER ASSET NOTE: the hero uses /vis/stallion.mp4 per the founder ruling
 * 2026-07-26 (chosen over the /vis/pages-background.mp4 candidate that F15c had
 * named alongside it), with /vis/stallion.jpg as its poster. It falls back to
 * that poster on mobile / reduced motion (S.media builds the poster sibling).
 * If the founder prefers a different clip, swap this one line — every /vis/*.mp4
 * has a sibling poster. */
function renderReservation() {
  return {
    n: "section", c: "egt-section egt-canvas egt-canvas--center egt-ty__arrival egt-ty__order",
    id: "your-reservation",
    i: [
      S.media({ video: "/vis/stallion.mp4", poster: "/vis/stallion.jpg", width: "1920", height: "1080", parallax: 0.06, priority: true }),
      { n: "div", c: "egt__shell egt-canvas__inner",
        i: [ renderLoadingCard(), renderOrderCard(), renderMissingCard() ] }
    ]
  };
}

/* THE NEUTRAL / LOADING CARD — what ships in the HTML, what a no-JS visitor
 * keeps, and what stays up while the client reads the session. It cannot be the
 * personal card (that one greets a specific person), so the page opens neutral
 * and only becomes personal once the client confirms whose reservation this is.
 * Every line here is true for anyone. */
function renderLoadingCard() {
  return withAttrs(S.glass({ width: "wide", tone: "dark", c: "egt-ty__card egt-ty__card--arrival",
    content: [
      { n: "p", c: "egt__eyebrow", i: "The Universe Platform by EGT" },
      /* h2, not h1 (SEO wave 2026-10-09, 2A-5): the page has ONE h1, the
       * order card's "You're in.", which matches the page title. Same class, so
       * it looks identical. */
      { n: "h2", c: "egt-ty__title", i: [ "Thank ", { n: "span", c: "egt__gold", i: "you." } ] },
      { n: "p", c: "egt-ty__lead",
        i: "Your founding reservation holds your place for self-serve access as the platform opens — you're first in line, ahead of everyone else." },
      { n: "p", c: "egt-ty__loading", role: "status", i: "Bringing up your reservation…" },
      { n: "noscript",
        i: { n: "p", c: "egt-ty__lead",
          i: "Your reservation is confirmed. The details are in the confirmation email we sent when you reserved your spot." } }
    ] }), { "data-ty-state": "loading" });
}

/* THE INTEGRATED ORDER CARD — revealed only once the client has read the
 * session. The arrival ("You're in") and the receipt (date, who it's for, the
 * $9.57 line, what's next) live in ONE card. This is also what prints (the
 * @media print block in egt.exploit.thankyou.scss reflows the same `.egt-ty__*`
 * classes to ink-on-white), so the printed page is a complete record.
 *
 * The $9.57 total is the single gold value on the card — the scarce accent that
 * says this is the thing that mattered. */
function renderOrderCard() {
  return withAttrs(S.glass({ width: "wide", tone: "dark", c: "egt-ty__card egt-ty__card--arrival",
    content: [
      { n: "p", c: "egt__eyebrow", i: "The Universe Platform by EGT" },
      { n: "h1", c: "egt-ty__title",
        i: [ "You're ", { n: "span", c: "egt__gold", i: "in." } ] },

      { n: "p", c: "egt-ty__lead",
        i: "Your founding reservation is confirmed. You're first in line for self-serve access as the platform opens — we email you the moment your sandbox is ready." },

      /* THE RECEIPT — the same card, continued. */
      { n: "h2", c: "egt-ty__h2 egt-ty__receipt-h", i: "Here's what you reserved." },

      { n: "dl", c: "egt-ty__receipt-meta",
        i: [
          { n: "div", c: "egt-ty__meta-row",
            i: [ { n: "dt", i: "Reservation date" }, { n: "dd", i: slot("placedOn", "", "—") } ] },
          { n: "div", c: "egt-ty__meta-row",
            i: [ { n: "dt", i: "Reserved by" }, { n: "dd", i: slot("buyerName", "", "—") } ] }
        ] },

      { n: "div", c: "egt-ty__grid",
        i: [
          /* what + what it cost */
          { n: "div", c: "egt-ty__panel",
            i: [
              { n: "h3", c: "egt-ty__panel-h", i: "Your reservation" },
              { n: "ul", c: "egt-ty__lines",
                i: [
                  { n: "li", c: "egt-ty__line",
                    i: [
                      { n: "span", c: "egt-ty__line-main",
                        i: { n: "span", c: "egt-ty__line-k", i: "Founding reservation — public beta" } },
                      { n: "span", c: "egt-ty__line-v", i: slot("amount", "", "$9.57") }
                    ] }
                ] },
              { n: "dl", c: "egt-ty__totals",
                i: [
                  { n: "div", c: "egt-ty__total-row egt-ty__total-row--sum",
                    i: [ { n: "dt", i: "Reserved today" }, { n: "dd", i: slot("total", "egt-ty__total-v", "$9.57") } ] }
                ] },
              { n: "p", c: "egt-ty__panel-note",
                i: "Non-refundable, and credited in full toward your launch." }
            ] },

          /* what's confirmed */
          { n: "div", c: "egt-ty__panel",
            i: [
              { n: "h3", c: "egt-ty__panel-h", i: "What's confirmed" },
              { n: "p", c: "egt-ty__panel-note",
                i: "Your founding place is held. You're provisioned ahead of everyone else when self-serve opens." },
              { n: "p", c: "egt-ty__panel-note egt-ty__panel-note--email",
                i: [ "Confirmation sent to ", slot("email", "egt-ty__email", "your inbox"), "." ] }
            ] }
        ] },

      /* Test-mode banner — revealed only when the session says so, because a test
       * reservation that LOOKS real is how a founder demo becomes a support
       * ticket. */
      { n: "p", c: "egt-ty__testbadge", "data-ty-test": "true", hidden: "hidden",
        i: "Test mode — no money moved for this reservation." },

      /* PRINT / SAVE — the browser's own print dialog, wired by the client via
       * addEventListener (never inline onclick; the CSP forbids it). Hidden on
       * the printed page by the @media print block. */
      { n: "div", c: "egt-ty__receipt-actions",
        i: { n: "button", type: "button", c: "egt-cta egt-cta--ghost egt-ty__print",
          "data-ty-print": "true", i: "Print / save receipt" } }
    ] }), { "data-ty-state": "order", hidden: "hidden" });
}

/* THE MISSING CARD — a fresh tab, a stale link, or a session that has nothing to
 * read. Warm, blameless, and it offers the confirmation email + a human. It must
 * never read as a fault: the buyer has reserved, and their record is safe. */
function renderMissingCard() {
  return withAttrs(S.glass({ width: "wide", tone: "dark", c: "egt-ty__card egt-ty__card--missing",
    content: [
      { n: "p", c: "egt__eyebrow", i: "This page" },
      { n: "h2", c: "egt-ty__h2", i: "We can't bring up a reservation here." },
      { n: "p", c: "egt-ty__lead",
        i: "This confirmation shows right after you reserve your spot. If you've reserved, nothing is wrong — your confirmation email has your founding place, and we'll email you again the moment your sandbox is ready." },
      { n: "p", c: "egt-ty__lead",
        i: "If you can't find that email, or something doesn't look right, tell us and we'll sort it out." },
      { n: "div", c: "egt-ty__actions",
        i: [
          { n: "a", c: "egt-cta egt-cta--primary", href: "/contact-us", i: "Contact the studio" },
          { n: "a", c: "egt-cta egt-cta--ghost", href: "/reserve", i: "Back to reserve" }
        ] }
    ] }), { "data-ty-state": "missing", hidden: "hidden" });
}

/* ── §02 WHAT HAPPENS NEXT ─────────────────────────────────────────────────
 * Plain language, in order, no dates we have not committed to. Three beats.
 * Shown in every state — it is true for anyone who has reserved, and it is the
 * answer to the question a lost link provokes. */
function renderNext() {
  var steps = [
    [ "Right now",
      "A confirmation is on its way to your inbox with your founding reservation. Keep it — it's your record." ],
    [ "As we build toward launch",
      "You hear from the team directly — the latest from the studio, and how the platform is coming together. Reservation holders are provisioned first." ],
    [ "When your sandbox is ready",
      "We email you your invitation. Your $9.57 is credited in full toward your launch — it simply held your place at the front of the line." ]
  ];

  return {
    n: "section", c: "egt-section egt-ty__next",
    i: { n: "div", c: "egt__shell",
      i: [
        { n: "p", c: "egt__eyebrow", i: "What happens next" },
        { n: "h2", c: "egt-section__title", i: "You don't need to do anything." },
        { n: "ol", c: "egt-ty__steps",
          i: steps.map(function (s) {
            return { n: "li", c: "egt-ty__step",
              i: [
                { n: "p", c: "egt-ty__step-k", i: s[0] },
                { n: "p", c: "egt-ty__step-v", i: s[1] }
              ] };
          }) }
      ] }
  };
}

/* ── §03 CLOSE ─────────────────────────────────────────────────────────────
 * A quiet close over imagery — while they wait, the one thing they can do now is
 * see what they've reserved a lane on. No upsell: they've already reserved. */
function renderClose() {
  return {
    n: "section", c: "egt-section egt-canvas egt-canvas--center egt-ty__close",
    i: [
      S.media({ img: "/imgs/egt-platform-canvas.jpg", alt: "", parallax: 0.06 }),
      { n: "div", c: "egt__shell egt-canvas__inner",
        i: S.glass({ width: "narrow", tone: "dark", c: "egt-ty__card",
          content: [
            { n: "p", c: "egt__eyebrow", i: "While you wait" },
            { n: "h2", c: "egt-ty__h2", i: "See what you've reserved." },
            { n: "p", c: "egt-ty__lead",
              i: "This is the platform your founding lane is on. Take a look around what you'll be building with when self-serve opens." },
            { n: "div", c: "egt-ty__actions",
              i: [
                { n: "a", c: "egt-cta egt-cta--primary", href: "/platform", i: "Explore the platform" },
                { n: "a", c: "egt-cta egt-cta--ghost", href: "/contact-us", i: "Contact the studio" }
              ] }
          ] }) }
    ]
  };
}
